import {
  getQuestionsByLevel,
  type ConnectLevel,
  type ConnectQuestion,
} from "@somethings-ui/core/connect-questions";
import { useCallback, useMemo, useRef } from "react";

interface InitialPicks {
  level1: ConnectQuestion;
  level2: ConnectQuestion;
  level3: ConnectQuestion;
}

interface ShufflerApi {
  initial: InitialPicks;
  shuffleOne: (level: ConnectLevel, excludeIds: string[]) => ConnectQuestion;
}

const RECENT_HISTORY_PER_LEVEL = 6;

export const useConnectQuestionShuffler = (): ShufflerApi => {
  const recentByLevelRef = useRef<Record<ConnectLevel, string[]>>({
    1: [],
    2: [],
    3: [],
  });

  const pickRandom = useCallback(
    (level: ConnectLevel, excludeIds: string[]): ConnectQuestion => {
      const pool = getQuestionsByLevel(level);
      const recent = recentByLevelRef.current[level];

      const strict = pool.filter(
        (q) => !excludeIds.includes(q.id) && !recent.includes(q.id),
      );
      let candidates = strict;
      // Recent history can starve a small pool — relax the exclude.
      if (candidates.length === 0) {
        candidates = pool.filter((q) => !excludeIds.includes(q.id));
      }
      if (candidates.length === 0) {
        candidates = pool;
      }

      const pick = candidates[Math.floor(Math.random() * candidates.length)];
      recent.push(pick.id);
      if (recent.length > RECENT_HISTORY_PER_LEVEL) {
        recent.shift();
      }
      return pick;
    },
    [],
  );

  const initial = useMemo<InitialPicks>(
    () => ({
      level1: pickRandom(1, []),
      level2: pickRandom(2, []),
      level3: pickRandom(3, []),
    }),
    [pickRandom],
  );

  const shuffleOne = useCallback(
    (level: ConnectLevel, excludeIds: string[]) => pickRandom(level, excludeIds),
    [pickRandom],
  );

  return { initial, shuffleOne };
};
